"use client" // Assuming this might be used in a Next.js App Router client component

import type React from "react"
import { useEffect } from "react"

// Define types for Chatwoot settings and SDK to ensure type safety
interface ChatwootSettings {
  hideMessageBubble: boolean
  position: "left" | "right"
  locale: string
  type: "standard" | "expanded_bubble"
  darkMode?: "auto" | "light" | "dark" // Added an optional dark mode setting
  launcherTitle?: string // Added an optional launcher title
}

interface ChatwootSDK {
  run: (options: { websiteToken: string; baseUrl: string }) => void
  // You can add other SDK methods here if needed
}

// Augment the global Window interface to include chatwootSettings and chatwootSDK
declare global {
  interface Window {
    chatwootSettings?: ChatwootSettings
    chatwootSDK?: ChatwootSDK
  }
}

interface ChatwootWidgetProps {
  websiteToken: string
  baseUrl: string
  locale?: string
  position?: "left" | "right"
  type?: "standard" | "expanded_bubble"
  hideMessageBubble?: boolean
  darkMode?: "auto" | "light" | "dark"
  launcherTitle?: string
}

const ChatwootWidget: React.FC<ChatwootWidgetProps> = ({
  websiteToken,
  baseUrl,
  locale = "en",
  position = "right",
  type = "standard",
  hideMessageBubble = false,
  darkMode = "auto",
  launcherTitle = "Chat with us",
}) => {
  useEffect(() => {
    // Ensure the script is not loaded multiple times, e.g., in development with React.StrictMode
    if (document.getElementById("chatwoot-sdk")) {
      return
    }

    // Add Chatwoot Settings
    window.chatwootSettings = {
      hideMessageBubble,
      position,
      locale,
      type,
      darkMode,
      launcherTitle,
    }

    // Paste the script from inbox settings except the <script> tag
    const d = document
    const t = "script"
    const g = d.createElement(t) as HTMLScriptElement
    const s = d.getElementsByTagName(t)[0]

    g.id = "chatwoot-sdk" // Add an ID to the script to check if it's already loaded
    g.src = `${baseUrl}/packs/js/sdk.js`
    g.async = true
    if (s && s.parentNode) {
      s.parentNode.insertBefore(g, s)
    } else {
      d.head.appendChild(g) // Fallback if no script tag is found
    }

    g.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: websiteToken,
          baseUrl: baseUrl,
        })
      }
    }

    // Cleanup function to remove the script and settings if the component unmounts
    // This might be useful in SPAs to prevent issues if the widget is removed and re-added.
    return () => {
      const sdkScript = document.getElementById("chatwoot-sdk")
      if (sdkScript && sdkScript.parentNode) {
        sdkScript.parentNode.removeChild(sdkScript)
      }
      delete window.chatwootSettings
      delete window.chatwootSDK // Assuming the SDK also cleans up its global state or this is desired
      // You might need to call a specific Chatwoot SDK cleanup function if available
      // e.g., if (window.chatwootSDK && typeof window.chatwootSDK.destroy === 'function') {
      //   window.chatwootSDK.destroy();
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteToken, baseUrl, locale, position, type, hideMessageBubble, darkMode, launcherTitle]) // Re-run if props change

  return null // This component does not render anything itself [^3]
}

export default ChatwootWidget
